import axios from 'axios';
import React, { useEffect, useMemo, useState } from 'react';
import {
    api, createShopBasketOrder, FieldControl, getEventById,
    getEventInfosByEventId,
    getFormToolControlsByEventId, getShopBasketOrder,
    addTicketsToOrder, removeTicketsFromOrder
} from "ticketino-api-client";
import FormToolControl from './FormToolControl';
import SummaryPage from './Utils/SummaryPage';
import { useTranslation } from 'react-i18next';

function Home() {

    const orderKey = "ticketino_form_order_id";
    const baseProxyUrl = process.env.REACT_APP_PLUGIN_PROXY;
    const htmlElement = document.getElementById("ticketinoAdvancedFormPlugin");

    const { t, i18n } = useTranslation();

    const [event, setEvent] = useState({});
    const [order, setOrder] = useState({});
    const [formToolControls, setFormToolControls] = useState([]);
    const [ticketsToAdd, setTicketsToAdd] = useState([]);
    const [optionsToAdd, setOptionsToAdd] = useState([]);

    const [pages, setPages] = useState(new Set());
    const [pageIndex, setPageIndex] = useState(0);

    const [loading, setLoading] = useState(true);
    const [displaySummary, setDisplaySummary] = useState(false);
    const [languageId, setLanguageId] = useState(true);

    //predefine the controls that we need to display
    const filterFormToolControls = (formToolControls, pages, pageIndex) => {

        if (pages.length < pageIndex) {
            return [];
        }

        //filter out the controls that are not in the current page
        const formToolControlsFilteredByPage = formToolControls.filter(ftc => ftc.pageNumber === pages[pageIndex]);

        //filter out the controls that are hidden by a ticket type 
        const ticketTypeIds = ticketsToAdd.length > 0 ? new Set(ticketsToAdd.map(t => t.ticketTypeId)) : new Set();
        const formToolControlsFilteredByTicketTypeId = formToolControlsFilteredByPage.filter(ftc => ftc.displayByTicketTypeId == null || ticketTypeIds.has(ftc.displayByTicketTypeId));

        //todo filter out the controls that are hidden by an option

        //todo filter out the controls that are hidden by an option value

        return formToolControlsFilteredByTicketTypeId;
    }

    const visibleFormToolControls = useMemo(
        () => filterFormToolControls(formToolControls, pages, pageIndex),
        [formToolControls, pages, pageIndex]
    );

    useEffect(() => {

        var languageISO = htmlElement.getAttribute("languageISO");

        var languageId = 1;

        switch (languageISO) {
            case 'en':
                languageId = 3;
                break;
            case 'fr':
                languageId = 2;
                break;
            case 'it':
                languageId = 4;
                break;
            default:
                languageId = 1;
        }

        setLanguageId(languageId);

        axios.get(`${baseProxyUrl}/token`).then((result) => {

            api.defaults.headers.common['Authorization'] = "Bearer " + result.data;

            var eventId = htmlElement.getAttribute("eventId");
            loadForm(eventId);
        });

    }, [])

    const onlyAllowOneOptionSelected = (formToolControl) => {

        const fieldControlsWithMoreThanOneOptionSelectable = [
            FieldControl.CheckboxList
        ];

        return fieldControlsWithMoreThanOneOptionSelectable.find(fc => fc === formToolControl.fieldControlId) == null;
    }

    const loadForm = async (eventId) => {
        try {

            //event
            const event = await getEventById(eventId);
            const infos = await getEventInfosByEventId(eventId);

            const updatedEventInfo = { ...event, info: infos };
            setEvent(updatedEventInfo);

            let formToolControls = await getFormToolControlsByEventId(eventId);
            formToolControls = formToolControls.map(ftc => {
                return {
                    ...ftc,
                    onlyAllowOneOptionSelected: onlyAllowOneOptionSelected(ftc),
                    values: []
                };
            }).sort((a, b) => (a.sortOrder > b.sortOrder) ? 1 : -1);

            setFormToolControls(formToolControls);

            const pages = [...new Set(formToolControls.map(ftc => ftc.pageNumber))];
            setPages(pages);

            await getOrder();

            setLoading(false);
        }
        catch (error) {
            setLoading(false);
            console.error(error);
        }
    }

    const createOrder = async () => {
        const order = await createShopBasketOrder({ currency: "CHF", tenantId: 1, pointOfSaleId: 6585 });
        return order;
    }

    const getOrder = async () => {

        const orderId = sessionStorage.getItem(orderKey);

        let order = null;

        if (orderId) {
            try {
                order = await getShopBasketOrder(parseInt(orderId));
            }
            // if it crashes create a new order, most likely the order doesn't exist in the server anymore
            catch (error) {
                order = await createOrder();
            }
        }
        else {
            order = await createOrder();
        }

        sessionStorage.setItem(orderKey, order.id);
        setOrder(order);

        return order;
    }

    const addFormToolControlValue = (formToolControlId, value) => {

        const updatedFormToolControls = formToolControls.map(ftc => {
            if (ftc.id === formToolControlId) {

                if (ftc.onlyAllowOneOptionSelected) {
                    ftc.values = [value];
                }
                else {
                    ftc.values.push(value);
                }

                return ftc;
            }
            else {
                return ftc;
            }
        });

        setFormToolControls(updatedFormToolControls);
    }

    const removeFormToolControlValue = (formToolControlId, value) => {

        const updatedFormToolControls = formToolControls.map(ftc => {
            if (ftc.id === formToolControlId) {
                ftc.values = ftc.values.filter(v => v !== value);
                return ftc;
            }
            else {
                return ftc;
            }
        });

        setFormToolControls(updatedFormToolControls);
    }

    const addTicket = async (ticket) => {

        const tickets = ticketsToAdd.filter(t => t.id !== ticket.id);

        const addTicketsToOrderApiModel = {
            ticketsToAdd: [
                {
                    ticketTypeId: ticket.ticketTypeId,
                    quantity: 1,
                    promotionCode: ticket.promotionCode
                }
            ]
        }

        const updatedOrder = await addTicketsToOrder(order.id, addTicketsToOrderApiModel);
        console.log(updatedOrder);

        //get the last ticket with the given ticket type
        //we can assume that it's the one that was added here
        var lastTicketCreated = updatedOrder.tickets
            .sort((a, b) => (a.bookedDate < b.bookedDate) ? 1 : -1)
            .find(t => t.ticketTypeId === ticket.ticketTypeId);

        ////when the ticket is still not defined in the list we must generate an id for it
        //if (ticket.id == null) {
        //    const id = Math.floor(Math.random() * 10000000);
        //    ticket = { ...ticket, lastTicketCreated.id };
        //}

        ticket = { ...ticket, id: lastTicketCreated.id };

        tickets.push(ticket);
        setTicketsToAdd(tickets);

        addFormToolControlValue(ticket.formToolControlId, ticket.value);

        return ticket;
    };

    const removeTicket = async (id) => {

        const ticket = ticketsToAdd.find(t => t.id === id);

        if (ticket?.formToolControlId == null) {
            return;
        }

        removeFormToolControlValue(ticket.formToolControlId, ticket.value);

        const filteredTicketsToAdd = ticketsToAdd.filter(t => t.id !== id);

        setTicketsToAdd(filteredTicketsToAdd);

        const updatedOrder = await removeTicketsFromOrder(order.id, [id]);
        console.log(updatedOrder);
    };

    const addOption = (option) => {

        let options = optionsToAdd.filter(o => o.field !== option.field);
        options.push(option);
        setOptionsToAdd(options);
        addFormToolControlValue(option.formToolControlId, option.value);

        return option;
    };

    const removeOption = (option) => {

        const filteredOptionsToAdd = optionsToAdd.filter(o => o.field !== option.field);

        removeFormToolControlValue(option.formToolControlId, option.value);
        setTicketsToAdd(filteredOptionsToAdd);
    };

    const setPage = (pageIndex) => {
        setPageIndex(pageIndex);

        //if the page selected is the next to the last page defined in the form we must display the summary
        if (pages.length == pageIndex) {
            setDisplaySummary(true);
        }
        else {
            setDisplaySummary(false);
        }
    }

    const onSubmit = (e) => {

        //prevent the submit behavior
        e.preventDefault();

        //check if there are more pages in the form
        if (pageIndex <= pages.length) {
            setPage(pageIndex + 1);
        }
        //last page -> submit order
        else {
            //create order

            //add tickets

            //add options
        }
    };

    const onGoBack = (e) => {

        if (pageIndex <= 0) {
            return;
        }

        setPage(pageIndex - 1);
    };

    return (
        <>
            {!loading && <form onSubmit={onSubmit} autoComplete="off">
                {visibleFormToolControls.map((ftc, index) => <FormToolControl formToolControl={ftc} language={languageId} order={order} addTicket={addTicket} removeTicket={removeTicket} addOption={addOption} removeOption={removeOption} />)}
                {displaySummary && <SummaryPage formToolControls={formToolControls} language={languageId} />}
                <div className="row mb-3 pt-4">
                    {pageIndex > 0 && <div className="col-md-2"><button className="btn btn-secondary me-3" type="button" onClick={onGoBack}>{t('Go Back')}</button></div>}
                    <div className="col-md-1"><input className="btn btn-primary" type="submit" value={t('Next')} /></div>
                </div>
            </form >}
        </>
    );
}

export default Home;